import { Link } from "react-router";
import { Plus } from "lucide-react";
import { SWRApiError } from "@/hooks/swr";
import { Button } from "@/components/ui/button";
import { SpecList } from "@/types/specList";
import { InfoAlert } from "../../shared/Alerts";
import { StepCardWrapper } from "./StepCardWrapper";

type ProjectSpecListStepProps = {
  error?: SWRApiError;
  isLoading: boolean;
  isLocked: boolean;
  masterSpecExists: boolean;
  openCreateModal: (open: boolean) => void;
  specLists: SpecList[];
};
const ProjectSpecListStep = ({
  error,
  isLoading,
  isLocked,
  masterSpecExists,
  openCreateModal,
  specLists,
}: ProjectSpecListStepProps) => {
  if (isLoading) {
    return (
      <StepCardWrapper title="Project Spec Lists">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
          Loading spec lists...
        </div>
      </StepCardWrapper>
    );
  }

  if (error) {
    return (
      <StepCardWrapper title="Project Spec Lists">
        <div className="text-sm text-red-600">Error loading spec lists</div>
      </StepCardWrapper>
    );
  }

  if (!masterSpecExists) {
    return (
      <StepCardWrapper title="Project Spec Lists">
        <InfoAlert>To continue a Master Version needs to be available.</InfoAlert>
      </StepCardWrapper>
    );
  }

  if (specLists.length === 0) {
    return (
      <StepCardWrapper title="Project Spec Lists">
        <InfoAlert>No Project Spec List linked.</InfoAlert>
        <Button disabled={isLocked} className="w-full" onClick={() => openCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          New Spec List
        </Button>
      </StepCardWrapper>
    );
  }

  const displayMasterSpec = (masterVersion?: SpecList["masterVersion"]) => {
    if (!masterVersion) return null;

    return (
      <p className="text-xs text-gray-600">
        Linked to master spec:{" "}
        <Link
          to={`/spec-lists/${masterVersion.specList?.id}/version/${masterVersion.id}`}
          className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium"
        >
          {masterVersion.specList.name}
        </Link>
      </p>
    );
  };

  return (
    <StepCardWrapper title="Project Spec Lists">
      <p className="text-sm font-medium text-gray-700">Associated Spec Lists:</p>
      {specLists.map((specList) => (
        <div key={specList.id} className="p-2 bg-gray-50 rounded-md">
          <p className="text-xs text-gray-600 mb-2">
            Name:{" "}
            <Link
              to={`/spec-lists/${specList.id}/version/${specList.latestVersion.id}`}
              className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium"
            >
              {specList.name}
            </Link>
          </p>
          <p className="text-xs text-gray-600 mb-2">
            Current active version: <b>v{specList.latestVersion.version}</b>
          </p>
          {displayMasterSpec(specList.masterVersion)}
        </div>
      ))}
      <Button className="w-full" onClick={() => openCreateModal(true)}>
        <Plus className="w-4 h-4 mr-2" />
        New Spec List
      </Button>
    </StepCardWrapper>
  );
};

export default ProjectSpecListStep;
